import { ScheduledFlagStop } from "src/app/types/flag-stop.type";
import { BuildServices, GenericRouteEvent, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from "./generic-route-event.model";
import { DateTime } from "luxon";
import * as helpers from '../../helpers/ui-helpers';
import { Capacitor } from "@capacitor/core";
import type { AlertOptions } from "@ionic/core";
import { FlagStopPerformPage, FlagStopPerformPageId } from "src/app/pages/flag-stop-perform/flag-stop-perform.page";
import { BaseJob } from "../job.type";
import { environment } from "src/environments/environment";

export class _ScheduledFlagStop extends GenericRouteEvent {

  public value: ScheduledFlagStop | BaseJob;

  constructor(value: BaseJob | ScheduledFlagStop, services: BuildServices) {
    super(services);
    this.type = 'flag-stop';

    const sfs = <ScheduledFlagStop>value;
    const job = <BaseJob>value;
    this.id = sfs.id || null;

    const scheduledStart = sfs.arrivalTime?.toString() || job.scheduledTime;
    this.scheduledStart = scheduledStart ? DateTime.fromISO(scheduledStart) : null;

    this.scheduledEnd = sfs.departureTime
      ? DateTime.fromISO(sfs.departureTime?.toString())
      : this.scheduledStart && job.stopDuration
        ? this.scheduledStart.plus({ seconds: job.stopDuration })
        : null;
    this.departedTime = value.departedTime ? DateTime.fromISO(value.departedTime.toString()) : null;

    this.completed = this.completed || environment.features.hasDepart
      ? !!this.departedTime
      : !!job.performTime || sfs.performed;
    this.location = typeof job.location === 'object'
      ? job.location
      : {
        name: sfs.location,
        address: sfs.name || this.translateSvc.translate('LABEL.blankHeadsign'),
        address2: sfs.direction ? `(${ sfs.direction})` : '',
        city: '',
        state: '',
        zipCode: '',
        coordinate: {
          longitude: sfs.longitude,
          latitude: sfs.latitude,
        },
      };

    this.stopSequence = job.sequence ?? 0;

    this.headerInfo = {
      time: helpers.getTimeForCardHeader({ start: this.scheduledStart, end: null, translateSvc: this.translateSvc }),
      type: sfs.timePoint ? 'LABEL.timePoint' : 'LABEL.scheduledStop',
      timeTo: this.scheduledStart?.toRelative() || '',
    };

    this.typeIcons = sfs.timePoint ? [helpers.TypeIcons.time_point] : [helpers.TypeIcons.flag_stop];

    this.value = value;

    this.updateTimeBasedProperties();
  }

  public updateTimeBasedProperties = () => {
    const timeToDiff = this.scheduledStart;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative({ unit: [ 'hours', 'minutes'  ], padding: 6000 });
  };

  public getActions(): RouteEventAction[] {
    return [
      {
        type: RouteEventActionTypeMap.perform,
        label: 'ACTION.quickPerform',
        icon: 'bus',
        color: RouteEventActionColorMap.secondary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        show: this.appData.route.allowQuickPerform,
        disabled: false,
        fn: (action: RouteEventAction) => this.quickPerform(action),
      },
      {
        type: RouteEventActionTypeMap.perform,
        label: 'ACTION.arrive',
        icon: 'locate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        show: !this.value?.performed && !this.value.arriveTime,
        disabled: false,
        fn: (action: RouteEventAction) => this.arrive({ action, checkLocation: true }),
      },
      {
        type: RouteEventActionTypeMap.depart,
        label: 'ACTION.depart',
        show: environment.features.hasDepart && this.value.arriveTime && !this.completed && !this.value.departedTime,
        icon: 'log-out', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.depart({ action }),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: 'ACTION.navigate',
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        show: helpers.hasValidGeo(this.location),
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        icon: 'map',
        color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        drivingMode: false,
        preview: true,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
    ].filter(action => action.show);
  }

  async quickPerform(action: RouteEventAction): Promise<void> {
    try {
      action.disabled = true;
      let proceed = false;
      const alertOptions: AlertOptions = {
        header: this.translateSvc.translate('LABEL.confirm'),
        message: `${this.translateSvc.translate('LABEL.confirmYouAt')} ${ this.value.location }`,
        backdropDismiss: false,
        buttons: [
          {
            text: this.translateSvc.translate('LABEL.no'),
            role: 'cancel',
            cssClass: 'danger',
            handler: () => { proceed = false; },
          }, {
            text: this.translateSvc.translate('LABEL.yes'),
            cssClass: 'primary',
            handler: () => { proceed = true; },
          },
        ],
      };
      const alert = await this.alertController.create(alertOptions);
      await alert.present();
      await alert.onDidDismiss();
      if (!proceed) { return; }
      if (environment.features.normalizedUpdates) {
        const job = this.value as BaseJob;
        job.performTime = DateTime.now().toISO();
      } else {
        const sfs = this.value as ScheduledFlagStop;
        await this.dispatchSvc.performRiderTypesFlagStop({ flagStop: sfs });
        sfs.performed = true;
      }
      if (!environment.features.hasDepart) {
        this.completed = true;
      }
    } catch (error) {
      this.notificationSvc.error(error?.message || error);
    } finally {
      action.disabled = false;
    }
  }

  arrive = async ({ action, checkLocation }) => {
    try {
      action.disabled = true;
      if (helpers.hasValidGeo(this.location)) {
        const hasArrived = checkLocation ? await this.locationSvc.checkHasArrived([this as GenericRouteEvent]) : true;
        if (!hasArrived) { return; }
      }

      // if we are on an ecolane normal type fixed route stop, we don't do any performs
      if (!environment.features.alwaysShowFlagStopForm && !this.appData.route.flagStopsEnabled){
        const odometerUpdated = await helpers.presentOdometer(this.modalController);
        if (odometerUpdated) {
          await this.dispatchSvc.arriveJobs([ this ]);
          this.value.arriveTime = DateTime.now().toISO();
          this.value.performed = true;
        }
        return;
      }

      // if on a type that supports flag stops, we arrive and perform from the modal
      const modal = await this.modalController.create({
        component: FlagStopPerformPage,
        componentProps: { scheduledFlagStop: this.value, _scheduledFlagStop: this },
        cssClass: 'parascope-full-modal',
        id: FlagStopPerformPageId,
      });
      await modal.present();
      const dialog = modal.shadowRoot.querySelector('[role="dialog"]');
      dialog.setAttribute('aria-label', this.translateSvc.translate('LABEL.flagStop'));
      const { role } = await modal.onDidDismiss();
      if (role !== 'submit') { return; }

      this.value.performed = true;
      const job = this.value as BaseJob;
      job.performTime = DateTime.now().toISO();

      if (!environment.features.hasDepart) {
        this.completed = true;
      }
    } catch (error) {
      this.notificationSvc.error(error?.message || error);
    } finally {
      action.disabled = false;
    }
  };

  depart = async ({ action }) => {
    try {
      if (action) action.disabled = true;
      await this.dispatchSvc.departJobs([ this ]);
      this.completed = true;
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };
}
